import React, { useState, useRef, useEffect } from 'react';
import { useStore } from '../store/useStore';
import { db } from '../db/db';
import { useEnterAsTab } from '../hooks/useEnterAsTab';
import { AppModal } from '../components/AppModal';

const Row = ({ label, children }: any) => (
  <div className="flex items-center mb-2">
    <span className="w-[260px] text-[13px] text-gray-800 flex-shrink-0">{label}</span>
    <span className="w-[14px] text-center font-bold text-gray-700 flex-shrink-0">:</span>
    <div className="flex-1 min-w-0">{children}</div>
  </div>
);

export const VoucherTypeCreationView = ({ config, handleItemSelect, voucherTypeId }: any) => {
  const containerRef  = useRef<HTMLDivElement>(null);
  const nameInputRef  = useRef<HTMLInputElement>(null);
  const activeCompany = useStore((state) => state.activeCompany);

  const [name, setName]                         = useState('');
  const [numberingMethod, setNumberingMethod]   = useState<'auto' | 'manual'>('auto');
  const [startingNumber, setStartingNumber]     = useState<number | ''>(1);
  const [currentNumber, setCurrentNumber]       = useState<number | ''>(1);
  const [affectsStock, setAffectsStock]         = useState(false);
  const [stockDirection, setStockDirection]     = useState<'In' | 'Out' | 'None'>('None');
  const [isSystem, setIsSystem]                 = useState(false);
  const [accepting, setAccepting]               = useState(false);

  useEnterAsTab(containerRef);

  useEffect(() => {
    if (!accepting) return;
    const h = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'y' || e.key === 'Enter') { e.preventDefault(); e.stopImmediatePropagation(); doSave(); }
      else if (e.key.toLowerCase() === 'n' || e.key === 'Escape') { e.preventDefault(); e.stopImmediatePropagation(); setAccepting(false); }
    };
    window.addEventListener('keydown', h, { capture: true });
    return () => window.removeEventListener('keydown', h, { capture: true });
  }, [accepting]);

  useEffect(() => {
    if (voucherTypeId) {
      db.voucherTypes.get(voucherTypeId).then(vt => {
        if (vt) {
          setName(vt.name); setNumberingMethod(vt.numberingMethod);
          setStartingNumber(vt.startingNumber); setCurrentNumber(vt.currentNumber);
          setAffectsStock(vt.affectsStock); setStockDirection(vt.stockDirection || 'None');
          setIsSystem(vt.isSystem);
        }
      });
    } else {
      setName(''); setNumberingMethod('auto'); setStartingNumber(1); setCurrentNumber(1);
      setAffectsStock(false); setStockDirection('None'); setIsSystem(false);
    }
  }, [voucherTypeId]);

  useEffect(() => { if (nameInputRef.current) nameInputRef.current.focus(); }, [voucherTypeId]);

  const doSave = async () => {
    setAccepting(false);
    if (!name.trim() || !activeCompany) { alert('Name is required'); return; }
    if (startingNumber === '' || Number(startingNumber) <= 0) { alert('Starting number must be greater than 0'); return; }
    try {
      const existingTypes = await db.voucherTypes.where('companyId').equals(activeCompany.id!).toArray();
      const nameExists = existingTypes.some(v => v.name.toLowerCase() === name.trim().toLowerCase() && v.id !== voucherTypeId);
      if (nameExists) { alert('A voucher type with this name already exists.'); return; }

      await db.transaction('rw', [db.voucherTypes, db.vouchers, db.auditLogs], async () => {
        let auditAction: 'CREATE' | 'UPDATE' = 'UPDATE';
        let affectedId = voucherTypeId;
        if (voucherTypeId) {
          const oldType = existingTypes.find(v => v.id === voucherTypeId);
          await db.voucherTypes.update(voucherTypeId, {
            name: name.trim(), prefix: '', numberingMethod,
            startingNumber: Number(startingNumber), currentNumber: Number(currentNumber),
            affectsStock, stockDirection: affectsStock ? stockDirection : 'None'
          });
          if (oldType && oldType.name.toLowerCase() !== name.trim().toLowerCase()) {
            const impacted = await db.vouchers.where({ companyId: activeCompany.id!, type: oldType.name }).toArray();
            for (const v of impacted) await db.vouchers.update(v.id!, { type: name.trim() });
          }
        } else {
          auditAction = 'CREATE';
          const exists = await db.voucherTypes.where('[companyId+name]').equals([activeCompany.id!, name.trim()]).first();
          if (exists) throw new Error('DUPLICATE_VTYPE');
          affectedId = await db.voucherTypes.add({
            companyId: activeCompany.id!, name: name.trim(), prefix: '', numberingMethod,
            startingNumber: Number(startingNumber), currentNumber: Number(startingNumber),
            affectsStock, stockDirection: affectsStock ? stockDirection : 'None',
            isSystem: false, createdAt: new Date().toISOString()
          }) as number;
        }
        await db.auditLogs.add({
          companyId: activeCompany.id!, actionType: auditAction, entityType: 'voucherType',
          entityId: affectedId!, timestamp: new Date().toISOString(),
          details: `${auditAction === 'CREATE' ? 'Created' : 'Updated'} voucher type ${name.trim()}`
        });
      });

      if (voucherTypeId) {
        if (handleItemSelect) handleItemSelect({ name: 'Quit' });
      } else {
        setName(''); setNumberingMethod('auto'); setStartingNumber(1); setCurrentNumber(1);
        setAffectsStock(false); setStockDirection('None');
        nameInputRef.current?.focus();
      }
    } catch (error: any) {
      if (error?.message === 'DUPLICATE_VTYPE') { alert('A voucher type with this name already exists.'); return; }
      alert(error?.message || 'Failed to save voucher type');
    }
  };

  const handleSave = () => setAccepting(true);

  const [pendingDelete, setPendingDelete] = useState(false);

  const handleDelete = async () => {
    if (!voucherTypeId || isSystem || !activeCompany) return;
    try {
      const inUse = await db.vouchers.where({ companyId: activeCompany.id!, type: name }).count();
      if (inUse > 0) { alert('Cannot delete this voucher type because it is being used in existing transactions.'); return; }
      setPendingDelete(true);
    } catch (error: any) { alert(error?.message || 'Failed to delete voucher type'); }
  };

  const confirmDelete = async () => {
    setPendingDelete(false);
    if (!voucherTypeId || !activeCompany) return;
    try {
      await db.transaction('rw', [db.voucherTypes, db.auditLogs], async () => {
        await db.voucherTypes.update(voucherTypeId, { isDeleted: true, deletedAt: new Date().toISOString(), deletedBy: 'System' } as any);
        await db.auditLogs.add({
          companyId: activeCompany.id!, actionType: 'DELETE', entityType: 'voucherType',
          entityId: voucherTypeId, timestamp: new Date().toISOString(), details: `Deleted voucher type ${name}`
        });
      });
      if (handleItemSelect) handleItemSelect({ name: 'Quit' });
    } catch (error: any) { alert(error?.message || 'Failed to delete voucher type'); }
  };

  const quit = () => { if (handleItemSelect) handleItemSelect({ name: 'Quit' }); };


  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 backdrop-blur-sm font-sans text-[13px]">
      <div ref={containerRef} className="flex flex-col bg-white relative overflow-hidden border border-[#444] rounded-sm"
        style={{ width: '680px', maxWidth: '98vw', maxHeight: '94vh' }}>

        <div className="bg-[#1e8585] text-white flex justify-between px-3 py-1 font-bold shadow-sm z-10 text-[14px] flex-shrink-0">
          <span className="w-1/3 text-left">{voucherTypeId ? 'Voucher Type Alteration' : 'Voucher Type Creation'}</span>
          <span className="w-1/3 text-center uppercase">{activeCompany?.name || ''}</span>
          <span className="w-1/3 text-right cursor-pointer hover:text-gray-300" data-quit-btn onClick={quit}>✕</span>
        </div>

        <div className="flex border-b border-[#c8d4d4] bg-[#f9fafb] flex-shrink-0 p-4">
          <div className="flex items-center w-full">
            <span className="w-[150px] flex-shrink-0">Name</span>
            <span className="w-[14px] text-center font-bold flex-shrink-0">:</span>
            <input ref={nameInputRef} type="text" value={name} onChange={e => setName(e.target.value)}
              disabled={isSystem}
              className={`flex-1 max-w-[380px] border border-[#a0c5e8] bg-[#fff9c4] px-1 py-[2px] outline-none shadow-inner text-black font-semibold ${isSystem ? 'opacity-60 cursor-not-allowed' : ''}`}
              autoFocus />
          </div>
        </div>

        <div className="flex-1 p-4 overflow-y-auto bg-[#fdfdfd]">
          <Row label="Method of Voucher Numbering">
            <select value={numberingMethod} onChange={e => setNumberingMethod(e.target.value as any)}
              className="border-b border-gray-300 outline-none bg-transparent text-black font-semibold w-full max-w-[200px] focus:bg-yellow-50">
              <option value="auto">Automatic</option>
              <option value="manual">Manual</option>
            </select>
          </Row>
          <Row label="Starting Number">
            <input type="number" value={startingNumber} onChange={e => setStartingNumber(e.target.value ? Number(e.target.value) : '')}
              className="border-b border-gray-300 outline-none bg-transparent text-black font-semibold w-[100px] focus:bg-yellow-50 text-right" />
          </Row>
          {voucherTypeId && (
            <Row label="Current Number">
              <input type="number" value={currentNumber} onChange={e => setCurrentNumber(e.target.value ? Number(e.target.value) : '')}
                className="border-b border-gray-300 outline-none bg-transparent text-black font-semibold w-[100px] focus:bg-yellow-50 text-right" />
            </Row>
          )}
          <Row label="Affects Stock">
            <select value={affectsStock ? 'Yes' : 'No'} onChange={e => setAffectsStock(e.target.value === 'Yes')}
              className="border-b border-gray-300 outline-none bg-transparent text-black font-semibold w-full max-w-[200px] focus:bg-yellow-50">
              <option value="No">No</option>
              <option value="Yes">Yes</option>
            </select>
          </Row>
          {affectsStock && (
            <Row label="Direction of Stock Movement">
              <select value={stockDirection} onChange={e => setStockDirection(e.target.value as any)}
                className="border-b border-gray-300 outline-none bg-transparent text-black font-semibold w-full max-w-[200px] focus:bg-yellow-50">
                <option value="None">None</option>
                <option value="In">In (Increase Stock)</option>
                <option value="Out">Out (Decrease Stock)</option>
              </select>
            </Row>
          )}
        </div>

        <div className="flex justify-between items-center bg-[#ebebeb] border-t border-[#b8b8b8] px-4 py-2 text-[12.5px] flex-shrink-0 h-[33px]">
          <div />
          <div className="flex items-center ml-auto gap-2">
            {voucherTypeId && !isSystem && (
              <button type="button" onClick={handleDelete} tabIndex={-1}
                className="px-3 py-0.5 border border-gray-400 text-gray-700 bg-[#e0e0e0] hover:bg-[#d0d0d0] rounded text-[11.5px] font-semibold"
                title="Alt + D to Delete"><u>D</u>elete</button>
            )}
            <button type="button" onClick={handleSave} tabIndex={-1} className="px-4 py-1 bg-[#1e8585] text-white font-bold rounded text-[11.5px] hover:bg-[#166e6e] shadow-sm mr-2">✓ Accept (Ctrl+A)</button><span className="text-gray-500 italic text-[11px]">Enter = Next · Esc = Cancel</span>
          </div>
        </div>

        {accepting && (
          <div className="absolute inset-0 bg-black/10 z-50 flex items-center justify-center">
            <div className="border-2 border-[#1e8585] bg-white px-16 py-6 shadow-[8px_8px_0_rgba(30,133,133,0.25)] text-center min-w-[200px] rounded-sm">
              <span className="block text-[17px] font-bold mb-4 text-black">Accept ?</span>
              <div className="font-bold text-[15px]">
                <span className="text-[#1e8585] cursor-pointer hover:underline" onClick={doSave}><u>Y</u>es</span>
                &nbsp;&nbsp;or&nbsp;&nbsp;
                <span className="text-gray-500 cursor-pointer hover:underline" onClick={() => setAccepting(false)}><u>N</u>o</span>
              </div>
            </div>
          </div>
        )}
        <button data-save-btn onClick={handleSave} className="hidden" />

        <AppModal
          open={pendingDelete}
          title="Delete Voucher Type"
          type="warning"
          message="Are you sure you want to delete this custom voucher type?"
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={confirmDelete}
          onCancel={() => setPendingDelete(false)}
        />
      </div>
    </div>
  );
};
